'use client';

import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { motion } from 'framer-motion';
import {
  ArrowRight, Building2, CheckCircle2, Home, Key, ShieldCheck, Star, UserCheck, Users,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { fadeUp, staggerContainer } from '@/lib/motion';
import { useAuth } from '@/lib/AuthContext';
import { PageHero } from '@/components/sections/PageHero';
import { ConfierBienProfileCard } from '@/components/owners/ConfierBienProfileCard';

type OwnerProfileCardConfig = {
  type: 'vendeur' | 'bailleur' | 'agence' | 'mandataire';
  icon: LucideIcon;
  badge: string;
  title: string;
  subtitle: string;
  description: string;
  image: string;
  imageAlt: string;
  benefits: readonly string[];
  publishHref: string;
  guestHref?: string;
  guestActionLabel: string;
  authenticatedActionLabel: string;
  requiresAuth?: boolean;
  gradient: string;
};

// ── Profile cards data ────────────────────────────────────────────────────────
const PROFILES: ReadonlyArray<OwnerProfileCardConfig> = [
  {
    type: 'vendeur' as const,
    icon: Home,
    badge: 'Vente',
    title: 'Propriétaire Vendeur',
    subtitle: 'Je souhaite vendre mon bien',
    description:
      "Vous souhaitez vendre votre bien\u00a0? Confiez-le facilement et bénéficiez d'un accompagnement professionnel pour trouver les meilleurs acheteurs rapidement et en toute sécurité.",
    image:
      'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&w=1200&q=85',
    benefits: [
      'Estimation gratuite & sans engagement',
      'Mise en valeur éditoriale professionnelle',
      'Réseau de 380+ agences partenaires',
      "Conseiller dédié jusqu'à la signature",
    ],
    guestHref: '/auth/register?from=/nous-confier-un-bien&ownerType=vendeur',
    publishHref: '/publier?ownerType=vendeur',
    guestActionLabel: 'Créer mon compte vendeur',
    authenticatedActionLabel: 'Déposer mon bien à vendre',
    imageAlt: 'Villa haut de gamme pour propriétaire vendeur',
    gradient: 'from-zinc-900/90 via-zinc-900/55 to-zinc-900/15',
  },
  {
    type: 'bailleur' as const,
    icon: Key,
    badge: 'Location',
    title: 'Propriétaire Bailleur',
    subtitle: 'Je souhaite mettre en location',
    description:
      'Vous souhaitez louer votre bien\u00a0? Simplifiez la gestion locative grâce à une solution professionnelle et sécurisée pour trouver des locataires fiables et gérer efficacement.',
    image:
      'https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=1200&q=85',
    benefits: [
      'Sélection rigoureuse des locataires',
      'Gestion locative sécurisée',
      'Rédaction du bail incluse',
      'Suivi professionnel continu',
    ],
    guestHref: '/auth/register?from=/nous-confier-un-bien&ownerType=bailleur',
    publishHref: '/publier?ownerType=bailleur',
    guestActionLabel: 'Créer mon compte bailleur',
    authenticatedActionLabel: 'Déposer mon bien à louer',
    imageAlt: 'Appartement moderne destiné à la location',
    gradient: 'from-zinc-900/90 via-zinc-900/55 to-zinc-900/15',
  },
  {
    type: 'agence' as const,
    icon: Building2,
    badge: 'Pro',
    title: 'Agence Immobilière',
    subtitle: 'Je publie au nom de mon agence',
    description:
      'Vous êtes une agence immobilière ? Centralisez vos mandats, publiez rapidement et pilotez vos annonces avec un espace professionnel dédié.',
    image:
      'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1200&q=85',
    benefits: [
      'Espace de diffusion multi-annonces',
      'Gestion d’équipe et de portefeuille',
      'Suivi des performances en temps réel',
      'Accompagnement dédié aux professionnels',
    ],
    publishHref: '/publier?ownerType=agence',
    guestHref: '/auth/register?from=/nous-confier-un-bien&accountType=agency',
    guestActionLabel: 'Créer mon compte agence',
    authenticatedActionLabel: 'Accéder à l’espace agence',
    imageAlt: 'Façade de bureaux pour une agence immobilière',
    gradient: 'from-zinc-900/90 via-zinc-900/55 to-zinc-900/15',
  },
  {
    type: 'mandataire' as const,
    icon: UserCheck,
    badge: 'Mandat',
    title: 'Mandataire Indépendant',
    subtitle: 'Je gère mes mandats en autonomie',
    description:
      'Vous êtes mandataire indépendant ? Valorisez vos biens, suivez vos opportunités et diffusez vos annonces avec des outils pensés pour votre activité.',
    image:
      'https://images.unsplash.com/photo-1556155092-8707de31f9c4?auto=format&fit=crop&w=1200&q=85',
    benefits: [
      'Publication rapide de vos mandats',
      'Tableau de suivi des contacts',
      'Visibilité renforcée des annonces',
      'Support dédié aux indépendants',
    ],
    guestHref: '/auth/register?from=/nous-confier-un-bien&ownerType=mandataire',
    publishHref: '/publier?ownerType=mandataire',
    guestActionLabel: 'Créer mon compte mandataire',
    authenticatedActionLabel: 'Publier en tant que mandataire',
    imageAlt: 'Mandataire immobilier en rendez-vous client',
    gradient: 'from-zinc-900/90 via-zinc-900/55 to-zinc-900/15',
  },
] as const;

// ── Trust stats ───────────────────────────────────────────────────────────────
const TRUST_STATS = [
  { icon: Users, value: '12\u202f000+', label: 'propriétaires accompagnés' },
  { icon: ShieldCheck, value: '98\u00a0%', label: 'de satisfaction client' },
  { icon: Star, value: '380+', label: 'agences partenaires' },
  { icon: CheckCircle2, value: '24h', label: 'délai de première réponse' },
];

// ── Page ─────────────────────────────────────────────────────────────────────
export default function NousConfierUnBienPage() {
  const router = useRouter();
  const { user } = useAuth();
  const isAuthenticated = !!user;
  const redirectToLogin = () => router.push('/auth/login?from=/nous-confier-un-bien');

  return (
    <>
      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <PageHero
        icon={<Key className="h-3.5 w-3.5" aria-hidden />}
        subtitle="Propriétaires & agences"
        title={<>Confiez votre bien{' '}<em className="not-italic text-accent">en toute confiance.</em></>}
        description="Que vous soyez propriétaire ou agence, notre équipe vous accompagne à chaque étape pour valoriser et louer ou vendre votre bien."
      />



      {/* ── Profile selection ─────────────────────────────────────────────── */}
      <section className="bg-background py-16 md:py-24">
        <div className="container-page">
          {/* Auth notice for unauthenticated users */}
          {!isAuthenticated && (
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="mb-10 rounded-2xl border border-accent/25 bg-accent/5 px-6 py-4 flex items-start gap-3 max-w-2xl mx-auto justify-center"
            >
              <ShieldCheck className="h-5 w-5 text-accent shrink-0 mt-0.5" />
              <p className="text-sm text-ink-secondary">
                <span className="font-semibold text-ink">Connexion requise&nbsp;&mdash;</span>{' '}
                Pour déposer ou confier un bien, vous devez être connecté à votre espace
                personnel. Créez un compte gratuit ou connectez-vous ci-dessous.
              </p>
            </motion.div>
          )}

          {/* Authenticated welcome */}
          {isAuthenticated && (
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="mb-10 rounded-2xl border border-success/25 bg-success/5 px-6 py-4 flex items-start gap-3 max-w-2xl mx-auto justify-center"
            >
              <CheckCircle2 className="h-5 w-5 text-success shrink-0 mt-0.5" />
              <p className="text-sm text-ink-secondary">
                <span className="font-semibold text-ink">
                  Bonjour, {user.firstName}&nbsp;&mdash;
                </span>{' '}
                Vous êtes connecté. Choisissez le type de mise en ligne souhaité.
              </p>
            </motion.div>
          )}

          {/* Cards grid */}
          <motion.div
            variants={staggerContainer(0.15, 0.1)}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: '0px 0px -8% 0px' }}
            className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto"
          >
            {PROFILES.map((profile) => {
              const actionLabel = isAuthenticated
                ? profile.authenticatedActionLabel
                : profile.guestActionLabel;
              const href = isAuthenticated
                ? profile.publishHref
                : (profile.requiresAuth ? undefined : profile.guestHref);
              const onClick = !isAuthenticated && profile.requiresAuth
                ? redirectToLogin
                : undefined;

              return (
                <motion.div
                  key={profile.type}
                  variants={fadeUp}
                  className="h-full"
                >
                  <ConfierBienProfileCard
                    title={profile.title}
                    subtitle={profile.subtitle}
                    description={profile.description}
                    icon={profile.icon}
                    badge={profile.badge}
                    benefits={profile.benefits}
                    image={profile.image}
                    imageAlt={profile.imageAlt}
                    gradient={profile.gradient}
                    actionLabel={actionLabel}
                    href={href}
                    onClick={onClick}
                    requiresAuth={profile.requiresAuth}
                    authBadgeLabel="Connexion requise"
                  />
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* ── Trust bar ─────────────────────────────────────────────────────── */}
      <section className="bg-surface-cream border-y border-border-subtle py-12">
        <div className="container-page">
          <motion.div
            variants={staggerContainer(0.08, 0)}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="grid grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {TRUST_STATS.map((stat) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={stat.label}
                  variants={fadeUp}
                  className="flex flex-col items-center text-center gap-2"
                >
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-accent/10 text-accent mb-1">
                    <Icon className="h-5 w-5" />
                  </span>
                  <span className="font-display text-2xl font-semibold text-ink tabular-nums">
                    {stat.value}
                  </span>
                  <span className="text-xs text-ink-secondary">{stat.label}</span>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* ── Bottom CTA ────────────────────────────────────────────────────── */}
      <section className="bg-gradient-to-br from-[#f7f4ef] via-white to-[#f2ede5] border-y border-border-subtle py-16 md:py-20">
        <div className="container-page text-center max-w-2xl mx-auto">
          <motion.div
            variants={staggerContainer(0.1)}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
          >
            <motion.p variants={fadeUp} className="eyebrow text-accent mb-4">
              Une question ?
            </motion.p>
            <motion.h2
              variants={fadeUp}
              className="font-display text-h2 leading-tight mb-4 text-ink"
            >
              Notre équipe vous répond.
            </motion.h2>
            <motion.p variants={fadeUp} className="text-ink-secondary mb-8 text-sm">
              Disponible du lundi au samedi, 9h–19h. Réponse garantie sous 24h.
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-wrap justify-center gap-3">
              <Button asChild variant="primary" size="lg">
                <Link href="/contact">
                  Nous contacter
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link href="/packs">Voir nos packs</Link>
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </>
  );
}
